import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff, Lock, User, Mail, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const LoginModal = ({ onClose }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'forgot-sent'
  const [form, setForm] = useState({ username: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 10);
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(timer); window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(form.username, form.password);
      onClose();
      navigate(data.role === 'admin' ? '/portal/admin' : '/portal');
    } catch (err) {
      setError(err.response?.data?.detail || t('login.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Notify team via Formspree
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '4deb5ebc-3d5b-446d-9890-9acae8b33da4',
          subject: 'Portal Password Reset Request',
          email: forgotEmail,
          message: `A portal user has requested a password reset for the email address: ${forgotEmail}`,
        }),
      });
    } catch (error) {
      console.error('Password reset request failed:', error);
      // Still show success message to prevent email enumeration
    }
    setLoading(false);
    setView('forgot-sent');
  };

  const inputCls = `w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:outline-none transition-all bg-transparent border text-gray-200 placeholder-gray-600`;

  const modalStyle = {
    width: '100%', maxWidth: 420, margin: '0 16px',
    backgroundColor: '#0a0a0a',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 4, overflow: 'hidden',
    boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 8px 60px rgba(255,255,255,0.08), 0 40px 100px rgba(0,0,0,0.8)',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.97)',
    transition: 'opacity 0.35s ease, transform 0.35s cubic-bezier(0.4,0,0.2,1)',
  };

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center"
      style={{ backdropFilter: visible ? 'blur(12px) brightness(0.4)' : 'none', transition: 'backdrop-filter 0.4s ease' }}
      onClick={onClose}
    >
      <div onClick={e => e.stopPropagation()} style={modalStyle}>

        {/* ── LOGIN VIEW ── */}
        {view === 'login' && (
          <>
            <div className="px-8 pt-8 pb-6 flex items-start justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#E8520A' }}>{t('login.portalLabel')}</p>
                <h2 className="text-xl font-black text-white" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.01em' }}>{t('login.heading')}</h2>
              </div>
              <button onClick={onClose} className="text-gray-600 hover:text-white transition-colors mt-1"><X size={18} /></button>
            </div>
            <div className="px-8 py-7">
              <p className="text-xs leading-relaxed mb-7" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {t('login.description')}
              </p>
              {error && (
                <div className="mb-5 rounded px-4 py-3 text-xs font-medium" style={{ backgroundColor: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.3)', color: '#f87171' }}>{error}</div>
              )}
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('login.username')}</label>
                  <div className="relative">
                    <User size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.25)' }} />
                    <input type="text" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} required className={inputCls}
                      style={{ borderColor: 'rgba(255,255,255,0.1)' }}
                      onFocus={e => (e.target.style.borderColor = '#E8520A')} onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      placeholder={t('login.usernamePlaceholder')} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('login.password')}</label>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.25)' }} />
                    <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required className={inputCls}
                      style={{ borderColor: 'rgba(255,255,255,0.1)', paddingRight: '2.5rem' }}
                      onFocus={e => (e.target.style.borderColor = '#E8520A')} onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      placeholder={t('login.passwordPlaceholder')} />
                    <button type="button" onClick={() => setShowPw(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.25)' }}
                      onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.25)')}>
                      {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading} className="w-full py-3 font-bold uppercase tracking-widest text-white text-xs transition-all duration-200 hover:opacity-85 disabled:opacity-40 mt-2"
                  style={{ backgroundColor: '#E8520A', borderRadius: 2 }}>
                  {loading ? t('login.signingIn') : t('login.accessPortal')}
                </button>
              </form>
              <div className="flex items-center justify-between mt-5">
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
                  {t('login.needAccess')}{' '}
                  <a href="/contact" className="transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.4)' }} onClick={onClose}>{t('login.contactUs')}</a>
                </p>
                <button onClick={() => { setError(''); setView('forgot'); }} className="text-xs transition-colors"
                  style={{ color: 'rgba(255,255,255,0.3)' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#E8520A')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.3)')}>
                  {t('login.forgotPassword')}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── FORGOT PASSWORD VIEW ── */}
        {view === 'forgot' && (
          <>
            <div className="px-8 pt-8 pb-6 flex items-start justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div>
                <button onClick={() => setView('login')} className="flex items-center gap-1.5 text-xs mb-3 transition-colors"
                  style={{ color: 'rgba(255,255,255,0.3)' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#fff')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.3)')}>
                  <ArrowLeft size={12} /> {t('login.backToLogin')}
                </button>
                <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#E8520A' }}>{t('login.passwordReset')}</p>
                <h2 className="text-xl font-black text-white" style={{ fontFamily: 'Inter, sans-serif' }}>{t('login.forgotHeading')}</h2>
              </div>
              <button onClick={onClose} className="text-gray-600 hover:text-white transition-colors mt-1"><X size={18} /></button>
            </div>
            <div className="px-8 py-7">
              <p className="text-xs leading-relaxed mb-7" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {t('login.forgotDescription')}
              </p>
              <form onSubmit={handleForgot} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('login.emailLabel')}</label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.25)' }} />
                    <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required
                      className={inputCls} style={{ borderColor: 'rgba(255,255,255,0.1)' }}
                      onFocus={e => (e.target.style.borderColor = '#E8520A')} onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      placeholder="your@email.com" />
                  </div>
                </div>
                <button type="submit" disabled={loading} className="w-full py-3 font-bold uppercase tracking-widest text-white text-xs transition-all hover:opacity-85 disabled:opacity-40"
                  style={{ backgroundColor: '#E8520A', borderRadius: 2 }}>
                  {loading ? t('login.sending') : t('login.submitRequest')}
                </button>
              </form>
            </div>
          </>
        )}

        {/* ── CONFIRMATION VIEW ── */}
        {view === 'forgot-sent' && (
          <>
            <div className="px-8 pt-8 pb-6 flex items-start justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#E8520A' }}>{t('login.requestReceived')}</p>
                <h2 className="text-xl font-black text-white" style={{ fontFamily: 'Inter, sans-serif' }}>{t('login.checkInbox')}</h2>
              </div>
              <button onClick={onClose} className="text-gray-600 hover:text-white transition-colors mt-1"><X size={18} /></button>
            </div>
            <div className="px-8 py-8 text-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: 'rgba(232,82,10,0.12)', border: '1px solid rgba(232,82,10,0.3)' }}>
                <Mail size={22} style={{ color: '#E8520A' }} />
              </div>
              <p className="text-white text-sm font-semibold mb-3">{t('login.requestConfirm')}</p>
              <p className="text-xs leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {t('login.requestConfirmDesc')} <span className="text-white">{forgotEmail}</span> {t('login.requestConfirmSuffix')}
              </p>
              <button onClick={onClose} className="px-8 py-3 font-bold uppercase tracking-widest text-white text-xs transition-all hover:opacity-85"
                style={{ backgroundColor: '#E8520A', borderRadius: 2 }}>
                {t('common.close')}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default LoginModal;
